import { useState } from "react";
import { Check, ChevronsUpDown, Building2, Plus, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CreateTeamModal } from "@/components/team/CreateTeamModal";
import { useUserTeams } from "@/lib/tanstack-query/users";
import type { TeamSummaryDto } from "@/lib/tanstack-query/dtos/team.dto";
import { useAuth } from "@/hooks/use-auth";

interface TeamSelectorProps {
  variant?: "desktop" | "mobile";
  buttonProps?: ButtonProps;
  currentTeam: TeamSummaryDto | null;
  onTeamSelect: (team: TeamSummaryDto | null) => void;
  onTeamCreate: (team: TeamSummaryDto) => void;
}

export const TeamSelector = ({
  variant = "desktop",
  buttonProps,
  currentTeam,
  onTeamSelect,
  onTeamCreate,
}: TeamSelectorProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch memberships with background refresh
  const { data: userTeams, refetch } = useUserTeams(user?.id);

  const handleTeamSelect = (team: TeamSummaryDto) => {
    onTeamSelect?.(team);
    setOpen(false);
  };

  const handlePersonalSelect = () => {
    onTeamSelect?.(null);
    setOpen(false);
  };

  const handleCreateTeam = () => {
    setOpen(false);
    setIsCreateModalOpen(true);
  };

  const handleMouseEnter = () => {
    // Refetch memberships in background on hover
    refetch();
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            onMouseEnter={handleMouseEnter}
            className={cn(
              "justify-between",
              variant === "mobile" ? "w-full" : "min-w-[180px] max-w-[240px]",
            )}
            {...buttonProps}
          >
            <div className="flex items-center gap-2 overflow-hidden">
              {currentTeam ? (
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate text-sm font-medium">
                {currentTeam?.name || "Personal Workspace"}
              </span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "p-0",
            variant === "mobile"
              ? "w-(--radix-popover-trigger-width)"
              : "max-w-[280px]",
          )}
          align="end"
        >
          <Command>
            <CommandList>
              <CommandEmpty>No teams found.</CommandEmpty>
              <CommandGroup>
                <CommandItem value={undefined} onSelect={handlePersonalSelect}>
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentTeam === null ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1">Personal Workspace</span>
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              {userTeams && userTeams.length > 0 && (
                <CommandGroup heading="Your Teams">
                  {userTeams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={team.name}
                      onSelect={() => handleTeamSelect(team)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          currentTeam?.id === team.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span className="flex-1 truncate">{team.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandSeparator />
              <CommandGroup>
                <CommandItem value="create-team" onSelect={handleCreateTeam}>
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="flex-1">Create Team</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <CreateTeamModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onTeamCreate={onTeamCreate}
      />
    </>
  );
};
